import axios from "axios";
import { adminPath } from "../config/adminRouting";
import { showErrorDialog } from "../components/DialogViewport";
import { cachedGet, clearGetRequestCache } from "./getRequestCache";

const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const localApiBaseUrl = `${window.location.protocol}//${window.location.hostname}:5000/api`;
const productionApiBaseUrl = "https://api.yanshtechnologies.in/api";

function cleanBaseUrl(url) {
  return String(url || "").trim().replace(/\/+$/, "");
}

function normalizeApiBaseUrl(url) {
  const cleaned = cleanBaseUrl(url);
  return cleaned === `${productionApiBaseUrl}/v1` ? productionApiBaseUrl : cleaned;
}

const fallbackApiUrls = String(process.env.REACT_APP_API_FALLBACK_URLS || "")
  .split(",")
  .map(normalizeApiBaseUrl)
  .filter(Boolean);

const apiBaseUrls = [
  normalizeApiBaseUrl(process.env.REACT_APP_API_URL),
  isLocalHost ? localApiBaseUrl : productionApiBaseUrl,
  ...fallbackApiUrls,
]
  .map(normalizeApiBaseUrl)
  .filter(Boolean)
  .filter((url, index, urls) => urls.indexOf(url) === index);

const API = axios.create({
  baseURL: apiBaseUrls[0],
  timeout: 15000
});

let followupSummaryCache = null;
let followupSummaryRequest = null;

export function getLeadFollowupSummary(options = {}) {
  const now = Date.now();
  const maxAgeMs = Math.max(0, Number(options.maxAgeMs ?? 30000));
  const authKey = localStorage.getItem("yansh_admin_token") || "";
  if (
    followupSummaryCache
    && followupSummaryCache.authKey === authKey
    && now - followupSummaryCache.loadedAt <= maxAgeMs
  ) {
    return Promise.resolve(followupSummaryCache.response);
  }
  if (followupSummaryRequest?.authKey === authKey) return followupSummaryRequest.promise;

  const request = API.get("/admin/lead-followups/summary", {
    suppressGlobalError: true,
  }).then((response) => {
    followupSummaryCache = { authKey, loadedAt: Date.now(), response };
    return response;
  }).finally(() => {
    if (followupSummaryRequest?.promise === request) followupSummaryRequest = null;
  });
  followupSummaryRequest = { authKey, promise: request };
  return request;
}

API.interceptors.request.use((config) => {
  config.__startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const token = localStorage.getItem("yansh_admin_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function isAdminAuthRequest(config = {}) {
  const url = String(config.url || "");
  return url === "/admin/login" || url === "/auth/login" || url === "/admin/session" || url === "/auth/session";
}

function shouldRetryWithNextApi(error) {
  if (isAdminAuthRequest(error.config)) return false;

  const status = error.response?.status;
  return !error.response || status === 502 || status === 503 || status === 504;
}

API.interceptors.response.use(
  (response) => {
    const startedAt = Number(response.config?.__startedAt || 0);
    if (startedAt && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("yansh:api-timing", { detail: {
        method: String(response.config?.method || "get").toUpperCase(),
        url: response.config?.url || "",
        durationMs: Math.round(((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt) * 10) / 10,
        status: response.status,
      } }));
    }
    return response;
  },
  async (error) => {
    const config = error.config || {};
    const currentIndex = Number(config.__apiBaseIndex || 0);
    const nextIndex = currentIndex + 1;

    if (shouldRetryWithNextApi(error) && nextIndex < apiBaseUrls.length) {
      config.__apiBaseIndex = nextIndex;
      config.baseURL = apiBaseUrls[nextIndex];
      return API.request(config);
    }

    const status = error.response?.status;
    const message = String(error.response?.data?.message || "").toLowerCase();
    const isAuthFailure = status === 401 || message.includes("authorization required") || message.includes("invalid token");

    if (isAuthFailure) {
      localStorage.removeItem("yansh_admin_token");
      window.history.replaceState({}, "", adminPath("/"));
      window.dispatchEvent(new Event("yansh:admin-session-expired"));
    } else if (!config.suppressGlobalError && error.code !== "ERR_CANCELED") {
      const errorMessage = error.response?.data?.message
        || (error.code === "ECONNABORTED" ? "The request timed out. Please try again." : error.message)
        || "The request could not be completed.";
      showErrorDialog(errorMessage, status ? `Request failed (${status})` : "Connection error");
    }

    return Promise.reject(error);
  }
);

const rawGet = API.get.bind(API);
API.get = (url, config = {}) => {
  const { cache, cacheTtlMs, forceRefresh, ...axiosConfig } = config;
  const normalizedUrl = String(url || "");
  const cacheAllowed = cache !== false
    && !normalizedUrl.startsWith("/whatsapp")
    && !normalizedUrl.includes("/share-status/")
    && !normalizedUrl.includes("/session")
    && normalizedUrl !== "/health";
  return cachedGet({
    url,
    config: { ...axiosConfig, cache: cacheAllowed, cacheTtlMs, forceRefresh },
    authKey: localStorage.getItem("yansh_admin_token") || "",
    request: () => rawGet(url, axiosConfig),
  });
};

["post", "put", "patch", "delete"].forEach((method) => {
  const rawMethod = API[method].bind(API);
  API[method] = async (...args) => {
    const response = await rawMethod(...args);
    clearGetRequestCache();
    return response;
  };
});

export default API;
